import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useCasePhotoUrl } from '@/application/usecases/useKits';
import { formatVersion } from '@/shared/format/version';
import { Navigate, Outlet, useLocation, useNavigationType } from 'react-router';
import { canOpenView, screenClosedReason, screenReadOnlyReason, type ViewKey } from '@/domain';
import { useHomeNotifications } from '@/features/notifications/useHomeNotifications';
import { useOutboxSyncDriver } from '@/application/container';
import { useIsOnline, useOutboxStatus } from '@/application/outbox/useOutboxSync';
import { useSession, useSignOut } from '@/application/usecases/useSession';
import type { Session } from '@/application/ports/AuthGateway';
import { IconChangedNotice, InstallPrompt } from '@/features/shell/InstallPrompt';
import { MoneyViewProvider } from '@/features/shell/MoneyViewProvider';
import { NotSyncedTray } from '@/features/shell/NotSyncedTray';
import { ScreenClosed, ScreenReadOnly } from '@/features/shell/AccessNotice';
import { NotificationCenter } from '@/features/notifications/NotificationCenter';
import { HomeNotifications } from '@/features/notifications/HomeNotifications';
import { NotificationPreferencesSheet } from '@/features/notifications/NotificationPreferencesSheet';
import { NotificationTicker } from '@/features/notifications/NotificationTicker';
import { useNotificationCenter } from '@/features/notifications/notificationCenterStore';
import { CaseInPlace } from '@/features/orders/CaseInPlace';
import { NOTICE_PARAM } from '@/features/orders/useCaseInPlace';
import { PhotoViewerHost } from '@/features/photos/PhotoViewerHost';
import { FindCaseDialog } from '@/features/shell/FindCaseDialog';
import { RunningStrip } from '@/features/shell/RunningStrip';
import { UpdateBanner } from '@/features/shell/UpdateBanner';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { useNotificationSummary } from '@/application/usecases/useNotifications';
import { useDeviceNotifications } from '@/application/usecases/useDeviceNotifications';
import { useLiveUpdates } from '@/application/usecases/useLiveUpdates';
import { SCREEN_TITLE, strings } from '@/shared/strings';
import { AppShell } from '@/ui/layout/AppShell';
import { PullToRefresh } from '@/ui/layout/PullToRefresh';
import { RouteTransition } from '@/ui/layout/RouteTransition';
import { isIPhoneBrowser, routeDirection, screenOf } from '@/ui/layout/routeDirection';
import { VIEW_PATH } from '@/ui/layout/navItems';
import { useConfirmStore } from '@/ui/primitives/confirmStore';
import { ROUTE_VIEWS } from './routes';
import { PageLoading } from '@/ui/feedback/Loading';

/**
 * The authenticated shell, and the route guard.
 *
 * Section 5.1: if a user opens a route not in `session.views`, redirect to
 * `session.homeView` silently. Do not show an error page. Roles change under
 * people's feet, a technician who was given the archive yesterday and lost it
 * this morning has done nothing wrong, and a redirect is the kinder behaviour.
 */
export function ShellLayout() {
  const { data: session, isPending } = useSession();
  const signOut = useSignOut();
  const location = useLocation();

  // A blank frame for the first moment rather than a spinner: the session
  // resolves from a warm cache in a few milliseconds, and a flashed spinner
  // reads as a fault. Past that the wait is real, a cold start or a slow
  // network, and a blank frame is what reads as a fault instead.
  if (isPending) return <PageLoading delayMs={400} label={strings.common.loadingSession} />;

  if (session === null || session === undefined) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /*
   * Signed in on the password everybody was given: a new one first, and nothing
   * else, not even the shell's own requests, which the server would refuse
   * until it is changed. Section 15 of the review of 2 October 2026.
   */
  if (session.mustChangePassword === true) {
    return (
      <ChangePasswordDialog onClose={() => undefined} forced={{ onSignOut: () => signOut.mutate() }} />
    );
  }

  /*
   * Amounts in dirhams, or in dollars where this device chose them, on every
   * screen and sheet of the shell. The lab's request of 17 October 2026, item 13.
   */
  return (
    <MoneyViewProvider>
      <SignedInShell session={session} />
    </MoneyViewProvider>
  );
}

function SignedInShell({ session }: { session: Session }) {
  // The live stream, for as long as somebody is signed in. Section 24 of the
  // review of 2 October 2026.
  useLiveUpdates();
  /*
   * A question waiting for its answer goes with the person who was asked. The
   * confirmation is held outside React and drawn outside this shell, so without
   * this it would still be on screen over the sign in page, and the next person
   * at a shared device could answer it. The request of 5 October 2026 (third),
   * section 2.
   */
  useEffect(() => () => useConfirmStore.getState().reset(), []);
  const signOut = useSignOut();
  const location = useLocation();
  const online = useIsOnline();
  const sync = useOutboxStatus();
  const driver = useOutboxSyncDriver();
  const [trayOpen, setTrayOpen] = useState(false);
  // The centre and a person's settings, opened by the bell and from the home screen alike.
  const center = useNotificationCenter();
  const [findOpen, setFindOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  // Counted by the server over everything, not over the page of them a list holds.
  const unread = useNotificationSummary();
  // Whether this person keeps their latest notifications on their home screen.
  const homeNotices = useHomeNotifications();
  // What management marked for push, shown by this device where its owner allowed it.
  useDeviceNotifications();
  // Neither panel outlives the session that opened it.
  useEffect(
    () => () => {
      useNotificationCenter.getState().hide();
      useNotificationCenter.getState().hideSettings();
    },
    [],
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The person's photograph for the menu, fetched with the token like any other.
  const avatarSrc = useCasePhotoUrl(session.user.avatarUrl ?? undefined);

  const view = viewForPath(location.pathname);
  const home = VIEW_PATH[session.homeView];

  // The page change: which way, from the path before this one.
  const navigationType = useNavigationType();
  const lastPath = useRef<string | null>(null);
  const shownPath = useRef(location.pathname);
  if (shownPath.current !== location.pathname) {
    lastPath.current = shownPath.current;
    shownPath.current = location.pathname;
  }
  const direction = routeDirection(lastPath.current, location.pathname, navigationType, isIPhoneBrowser());

  if (view !== null && !canOpenView(session.views, view)) {
    return <Navigate to={home} replace />;
  }

  /*
   * A screen held without the permission its work rests on. The lab's request
   * of 11 October 2026: said in place of the screen, or above it where it can
   * still be read, instead of a list that never arrives.
   */
  const closed = view === null ? null : screenClosedReason(view, session.permissions);
  const readOnly = view === null ? null : screenReadOnlyReason(view, session.permissions);

  return (
    <AppShell
      title={location.pathname.startsWith('/profile') ? strings.profile.title : SCREEN_TITLE[view ?? session.homeView]}
      avatarSrc={avatarSrc}
      version={formatVersion()}
      onOpenProfile={() => void navigate('/profile')}
      role={session.role}
      views={session.views}
      userName={session.user.name}
      userTitle={session.user.title}
      canScan={canOpenView(session.views, 'scan')}
      unreadCount={unread.data?.unread ?? 0}
      offline={!online}
      notSyncedCount={sync.pending + sync.failed}
      notSyncedFailed={sync.failed > 0}
      onOpenNotSynced={() => setTrayOpen(true)}
      onOpenNotifications={center.show}
      onOpenSearch={() => setFindOpen(true)}
      onChangePassword={() => setPasswordOpen(true)}
      onSignOut={() => signOut.mutate()}
      /*
       * The running tasks, on every screen. Section 2.4 of the request of
       * 20 September 2026.
       *
       * Mounted for everybody rather than for the two bench roles alone: a
       * manager works every bench and can start a clock, and the strip draws
       * nothing at all when nothing is running, so there is nothing to gate.
       *
       * The update offer goes above it, for everybody: it draws nothing until a
       * newer build is on the server.
       */
      banner={
        <>
          <UpdateBanner />
          <RunningStrip />
        </>
      }
    >
      <RouteTransition transitionKey={screenOf(location.pathname)} scrollKey={location.key} direction={direction}>
        {/*
          The Notifications section, over whichever screen is this person's
          home: their latest few, of the types they chose to see there. The
          request of 7 October 2026, section 2.

          Since the lab's request of 12 October 2026 a Dashboard and the
          Overview place it themselves, as a widget their reader arranges, so
          it is drawn here only over a home that is neither: a technician's
          queue, or the first screen of somebody the Dashboard was taken from.
          And only for somebody who has not taken it off, which its heading
          lets them do and their notification settings let them undo.
        */}
        {/*
          The important ones on one moving line, over a person's home screen and
          over the two screens where work is reviewed. The request of
          8 October 2026 (fourth).
        */}
        {(location.pathname === home || TICKER_PATHS.includes(location.pathname)) && <NotificationTicker />}
        {location.pathname === home && homeNotices.plain && homeNotices.ready && homeNotices.shown && (
          <HomeNotifications onHide={homeNotices.hide} />
        )}
        {readOnly !== null && <ScreenReadOnly reason={readOnly} />}
        {closed === null ? <Outlet /> : <ScreenClosed reason={closed} />}
      </RouteTransition>
      {/* Pull down at the top of any screen to refresh what it shows. Request of 4 October 2026, section 8. */}
      <PullToRefresh onRefresh={() => queryClient.refetchQueries({ type: 'active' })} />
      {/*
        The one image viewer, opened from wherever a photograph is. Here and not
        inside the page that was tapped: a sheet or a drawer is transformed
        while it moves, which would confine it, and a click inside it would
        reach the sheet's backdrop. The request of 5 October 2026 (third),
        section 6.
      */}
      <PhotoViewerHost />
      <InstallPrompt />
      <IconChangedNotice />
      {trayOpen && <NotSyncedTray driver={driver} onClose={() => setTrayOpen(false)} />}
      {center.open && <NotificationCenter onClose={center.hide} />}
      {center.settingsOpen && <NotificationPreferencesSheet onClose={center.hideSettings} />}
      {/* The case a notification opened, in place, over whatever screen is showing. */}
      <CaseInPlace param={NOTICE_PARAM} returnLabel={strings.notifications.title} />
      {findOpen && <FindCaseDialog onClose={() => setFindOpen(false)} />}
      {passwordOpen && <ChangePasswordDialog onClose={() => setPasswordOpen(false)} />}
    </AppShell>
  );
}

/** The screens that carry the moving line of important notifications besides a person's home: Payments and Final Check. */
const TICKER_PATHS: readonly string[] = ['/payments', '/qc'];

/**
 * Which view a path belongs to. Longest pattern first, so `/line/:stage` is
 * matched as the stage detail rather than as the production line.
 */
function viewForPath(pathname: string): ViewKey | null {
  const match = ROUTE_VIEWS.find((route) => route.pattern.test(pathname));
  return match?.view ?? null;
}
